import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Button,
  Row,
  Col,
  Form,
  Table,
  Alert,
  Spinner,
  Badge,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faFileContract,
  faUser,
  faCalendarAlt,
  faDollarSign,
} from "@fortawesome/free-solid-svg-icons";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  orderBy,
} from "firebase/firestore";
import { useCollection } from "react-firebase-hooks/firestore";
import { initializeApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import {
  SHOPMY_PLACEHOLDER_NAME,
  isStranded,
  applyGrantToAccount,
  canApply,
  membershipTypeFor,
  NO_APP_ACCOUNT,
} from "./loftiePlusGrant";

// Firebase config
const firebaseConfig = {
  apiKey: "AIzaSyBBzepSPN2wdZfvan5GgyeeIREAV6k5BaU",
  authDomain: "loftie-4f472.firebaseapp.com",
  databaseURL: "https://loftie-4f472.firebaseio.com",
  projectId: "loftie-4f472",
  storageBucket: "loftie-4f472.appspot.com",
  messagingSenderId: "302939981606",
  appId: "1:302939981606:web:7fcb10ed6fe46fa437a3e7",
  measurementId: "G-L8VGQXX28P",
};

const firebaseApp = initializeApp(firebaseConfig);
const db = getFirestore(firebaseApp);
const auth = getAuth(firebaseApp);

// Server half of "Create grant for this order" (cloud_functions/membership_grants.py). The
// browser holds no Shopify token, so the order lookup lives there, and so does the write,
// because the two must agree on the same facts. It verifies the caller's Firebase ID token
// and the admins/{uid} document before it reads or writes anything.
const CREATE_GRANT_URL =
  "https://us-central1-loftie-4f472.cloudfunctions.net/create_membership_grant";

async function callCreateGrant(body) {
  const user = auth.currentUser;
  if (!user) throw new Error("No sign-in found. Reload the page and sign in again.");
  const token = await user.getIdToken();
  const res = await fetch(CREATE_GRANT_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  let data = null;
  try {
    data = await res.json();
  } catch (e) {
    data = null;
  }
  if (!res.ok || !data || data.ok === false) {
    const err = new Error(
      (data && data.error) || `The server answered ${res.status}. Nothing was changed.`
    );
    if (data && data.code) err.code = data.code;
    throw err;
  }
  return data;
}

// Both grant paths refuse an address with no app account (error.code no_app_account): a grant
// nobody can log into sits dormant and looks like it worked. The operator may override after
// reading exactly what that means; the contract is then stamped granted_without_app_account.
const confirmNoAppAccount = (err) =>
  window.confirm(
    `${err.message}\n\n` +
      `Write it anyway? Only if the customer has confirmed they will sign up with exactly this ` +
      `address. The contract will be marked "granted without app account" so CS can see it is waiting.`
  );

// Every way an order name shows up in order_id: as typed, without "#", with "#". Webhook
// grants are stored as "#376336"; Indiegogo orders are "igg-35" and never carry a "#".
const orderIdVariants = (term) => {
  const t = term.trim();
  const bare = t.startsWith("#") ? t.slice(1) : t;
  return Array.from(new Set([t, bare, `#${bare}`].filter(Boolean)));
};

const formatIsoDay = (iso) => (iso ? String(iso).slice(0, 10) : "N/A");

function SubscriptionContracts() {
  const [searchEmail, setSearchEmail] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [allContracts, setAllContracts] = useState([]);
  const [showAllContracts, setShowAllContracts] = useState(false);
  const [searchBy, setSearchBy] = useState("email");
  const [stranded, setStranded] = useState([]);
  const [strandedLoading, setStrandedLoading] = useState(false);
  const [showStranded, setShowStranded] = useState(false);
  // Which row is open for reassignment. A contract can appear in both tables at once, so
  // the panel is keyed by table as well as by contract — otherwise two panels render
  // against one set of form state.
  const [reassigning, setReassigning] = useState(null);
  const [reassignSource, setReassignSource] = useState(null);
  const [reassignEmail, setReassignEmail] = useState("");
  const [reassignName, setReassignName] = useState("");
  const [reassignBusy, setReassignBusy] = useState(false);
  const [reassignResult, setReassignResult] = useState(null);
  // "Create grant for this order": an order search that finds no contract offers to look
  // the order up in Shopify and create one. Preview first, then a confirmed apply. The
  // result stays on screen after the new row appears — it carries what to tell the customer.
  const [createPreview, setCreatePreview] = useState(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState("");
  const [createType, setCreateType] = useState("");
  const [createEmail, setCreateEmail] = useState("");
  const [createName, setCreateName] = useState("");
  const [createReason, setCreateReason] = useState("");
  const [createBusy, setCreateBusy] = useState(false);
  const [createResult, setCreateResult] = useState(null);

  // Hook to get all contracts for display
  const [contracts, contractsLoading, contractsError] = useCollection(
    collection(db, "subscription-contracts"),
    {
      snapshotListenOptions: { includeMetadataChanges: true },
    }
  );

  useEffect(() => {
    if (contracts) {
      const contractsData = contracts.docs.map((doc) => ({
        // Data last would let the contract's own numeric `id` field overwrite the
        // document id, and doc() then throws "indexOf is not a function". Doc id wins.
        ...doc.data(),
        id: doc.id,
      }));
      // Newest first. Every contract carries createdAt as an ISO string
      // (synced and granted alike); fall back to the document id.
      contractsData.sort((a, b) => {
        if (a.createdAt && b.createdAt) {
          return String(b.createdAt).localeCompare(String(a.createdAt));
        }
        // Fallback to simple comparison if localeCompare isn't available
        const aId = String(a.id || "");
        const bId = String(b.id || "");
        return bId.localeCompare(aId);
      });
      setAllContracts(contractsData);
      console.log("Loaded contracts:", contractsData.length, contractsData);
    }
    if (contractsError) {
      console.error("Error loading contracts:", contractsError);
    }
  }, [contracts, contractsError]);

  const handleSearch = async () => {
    if (!searchEmail.trim()) {
      setError(
        searchBy === "order"
          ? "Please enter an order number"
          : "Please enter an email address"
      );
      return;
    }

    setLoading(true);
    setError("");
    setHasSearched(true);

    try {
      // A stranded ShopMy grant does not carry the creator's email — the relay address is
      // on it — so searching by order number is the only way to reach one from a ticket.
      const term = searchEmail.trim();
      const q =
        searchBy === "order"
          ? query(
              collection(db, "subscription-contracts"),
              where("order_id", "in", orderIdVariants(term))
            )
          : query(
              collection(db, "subscription-contracts"),
              where("user.email", "==", term.toLowerCase())
            );

      const querySnapshot = await getDocs(q);
      const results = [];

      querySnapshot.forEach((doc) => {
        results.push({ ...doc.data(), id: doc.id });
      });

      setSearchResults(results);
    } catch (err) {
      setError("Error searching contracts: " + err.message);
      console.error("Error searching:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadStranded = async () => {
    setStrandedLoading(true);
    setError("");
    try {
      const querySnapshot = await getDocs(
        query(
          collection(db, "subscription-contracts"),
          where("user.name", "==", SHOPMY_PLACEHOLDER_NAME)
        )
      );
      const results = [];
      querySnapshot.forEach((doc) => results.push({ ...doc.data(), id: doc.id }));
      results.sort((a, b) =>
        String(b.order_id || "").localeCompare(String(a.order_id || ""))
      );
      setStranded(results);
    } catch (err) {
      setError("Error loading stranded grants: " + err.message);
      console.error("Error loading stranded grants:", err);
    } finally {
      setStrandedLoading(false);
    }
  };

  const openReassign = (contract, source) => {
    setReassigning(contract.id);
    setReassignSource(source);
    setReassignEmail("");
    setReassignName("");
    setReassignResult(null);
    setReassignBusy(false);
  };

  // Keep the row visible after a successful grant instead of dropping it. The panel that
  // carries the result — including whether the creator needs to sign up first — lives
  // inside that row, so removing it would destroy the only confirmation the operator gets.
  const markReassigned = (list, contractId, result) =>
    list.map((c) =>
      c.id === contractId
        ? { ...c, user: { ...(c.user || {}), email: result.email, name: result.name } }
        : c
    );

  const handleReassign = async (contract) => {
    const email = reassignEmail.trim().toLowerCase();
    const currentEmail = (contract.user?.email || "").trim().toLowerCase();
    const term = membershipTypeFor(contract) || "";
    // Matching a customer to a contract is done off-platform, from their ticket. A wrong
    // match hands one person's membership to someone else, so make the operator read it back.
    const confirmed = window.confirm(
      currentEmail === email
        ? `Turn on ${term} Loftie+ for the app account ${email}?\n\n` +
            `The contract on order ${contract.order_id || contract.id} already names that address, so only the app account changes.`
        : `Move the ${term} Loftie+ grant on order ${contract.order_id || contract.id} ` +
            `from ${contract.user?.email || "(no email)"} to ${email}?\n\n` +
            `Only do this if that address came from the customer themselves, in their ticket.`
    );
    if (!confirmed) return;

    setReassignBusy(true);
    setReassignResult(null);
    try {
      const args = {
        contractId: contract.id,
        email,
        name: reassignName,
        expectedEmail: contract.user?.email || "",
      };
      let result;
      try {
        result = await applyGrantToAccount(db, args);
      } catch (err) {
        if (err.code !== NO_APP_ACCOUNT || !confirmNoAppAccount(err)) throw err;
        result = await applyGrantToAccount(db, { ...args, allowNoAppAccount: true });
      }
      setReassignResult({ ok: true, ...result });
      setStranded((prev) => markReassigned(prev, contract.id, result));
      setSearchResults((prev) => markReassigned(prev, contract.id, result));
    } catch (err) {
      setReassignResult({ ok: false, message: err.message });
      console.error("Error reassigning contract:", err);
    } finally {
      setReassignBusy(false);
    }
  };

  const resetCreate = () => {
    setCreatePreview(null);
    setCreateLoading(false);
    setCreateError("");
    setCreateType("");
    setCreateEmail("");
    setCreateName("");
    setCreateReason("");
    setCreateBusy(false);
    setCreateResult(null);
  };

  const loadCreatePreview = async (emailOverride) => {
    setCreateLoading(true);
    setCreateError("");
    try {
      const data = await callCreateGrant({
        mode: "preview",
        order_name: searchEmail.trim(),
        email: emailOverride || undefined,
      });
      setCreatePreview(data);
      setCreateType(data.suggested_type || "");
      setCreateEmail(data.target_email || "");
      setCreateName((data.order && data.order.customer_name) || "");
      setCreateReason(data.suggested_reason || "");
    } catch (err) {
      setCreateError(err.message);
      setCreatePreview(null);
      console.error("Error previewing grant:", err);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleCreateGrant = async () => {
    if (!createPreview) return;
    const email = createEmail.trim().toLowerCase();
    const reason = createReason.trim();
    const term =
      createType === "LIFETIME"
        ? "Lifetime"
        : createType === "ANNUAL"
        ? "Annual"
        : "";
    if (!term) {
      setCreateError("Choose Lifetime or Annual.");
      return;
    }
    if (!email) {
      setCreateError("Enter the address the customer uses in the app.");
      return;
    }
    if (!reason) {
      setCreateError("Say why this order gets a grant. It is recorded on the contract.");
      return;
    }
    // The order and the address both have to come from the customer's ticket. A wrong
    // pairing hands one person's membership to someone else, so make the operator read it.
    const confirmed = window.confirm(
      `Create a ${term} Loftie+ grant for order ${createPreview.order.name} on ${email}?\n\n` +
        `Reason recorded on the contract: ${reason}\n\n` +
        `Only do this if the order number and the address both came from the customer's ticket.`
    );
    if (!confirmed) return;

    setCreateBusy(true);
    setCreateError("");
    try {
      const body = {
        mode: "apply",
        order_name: createPreview.order.name,
        email,
        name: createName.trim(),
        type: createType,
        reason,
      };
      let data;
      try {
        data = await callCreateGrant(body);
      } catch (err) {
        if (err.code !== NO_APP_ACCOUNT || !confirmNoAppAccount(err)) throw err;
        data = await callCreateGrant({ ...body, allow_no_app_account: true });
      }
      setCreateResult(data);
      setCreatePreview(null);
      // The new contract now answers the same search; show the row.
      await handleSearch();
    } catch (err) {
      setCreateError(err.message);
      console.error("Error creating grant:", err);
    } finally {
      setCreateBusy(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  const formatStatus = (status) => {
    const statusColors = {
      active: "success",
      inactive: "secondary",
      cancelled: "danger",
      pending: "warning",
    };
    return (
      <Badge bg={statusColors[status?.toLowerCase()] || "secondary"}>
        {status || "Unknown"}
      </Badge>
    );
  };

  const renderReassignPanel = (contract, source, colSpan) => {
    if (reassigning !== contract.id || reassignSource !== source) return null;
    return (
      <tr>
        <td colSpan={colSpan} style={{ backgroundColor: "#faf6f0" }}>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              handleReassign(contract);
            }}
          >
            <Row className="g-2 align-items-end">
              <Col md={5}>
                <Form.Label className="mb-1">Customer's email</Form.Label>
                <Form.Control
                  type="email"
                  required
                  value={reassignEmail}
                  onChange={(e) => setReassignEmail(e.target.value)}
                  placeholder="the address they log into the app with"
                />
              </Col>
              <Col md={4}>
                <Form.Label className="mb-1">Customer's name</Form.Label>
                <Form.Control
                  type="text"
                  required={isStranded(contract)}
                  value={reassignName}
                  onChange={(e) => setReassignName(e.target.value)}
                  placeholder={
                    isStranded(contract)
                      ? "e.g. Emily Bieberly"
                      : "optional, keeps the name on the contract if blank"
                  }
                />
              </Col>
              <Col md={3}>
                <Button
                  type="submit"
                  variant="info"
                  disabled={reassignBusy}
                  className="me-2"
                >
                  {reassignBusy
                    ? "Working..."
                    : `Apply ${membershipTypeFor(contract) || ""} Loftie+`}
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={() => setReassigning(null)}
                  disabled={reassignBusy}
                >
                  Cancel
                </Button>
              </Col>
            </Row>
          </Form>

          {reassignResult && (
            <Alert
              variant={reassignResult.ok ? "success" : "danger"}
              className="mt-3 mb-0"
            >
              {reassignResult.ok ? (
                <>
                  <strong>
                    {reassignResult.membershipType} Loftie+ is now on{" "}
                    {reassignResult.email}.
                  </strong>
                  {reassignResult.moved === false && (
                    <div className="mt-1">
                      The contract already named this address, so only the app
                      account changed.
                    </div>
                  )}
                  <div className="mt-1">
                    {reassignResult.linkedAppAccount
                      ? "Their app account is switched on — have them force-quit and reopen the app."
                      : `They have no Loftie app account yet. They need to download the app, sign up with exactly that address, and use “Link your purchase” — ${reassignResult.membershipType} turns on from there.`}
                  </div>
                </>
              ) : (
                <>
                  <strong>Not done.</strong> {reassignResult.message}
                </>
              )}
            </Alert>
          )}
        </td>
      </tr>
    );
  };

  // Offered only when an order search finds no contract. Preview shows the order as Shopify
  // has it plus every reason the grant must not be created; the form appears only when
  // there is none. The server repeats every check before it writes.
  const renderCreateGrant = () => {
    const previewEmail = (createPreview && createPreview.target_email) || "";
    const typedEmail = createEmail.trim().toLowerCase();
    const termLabel =
      createType === "LIFETIME"
        ? "Lifetime"
        : createType === "ANNUAL"
        ? "Annual"
        : "";
    return (
      <div
        className="mt-3"
        style={{
          backgroundColor: "#faf6f0",
          padding: "1rem",
          borderRadius: "6px",
        }}
      >
        <h5 style={{ color: "#635956" }}>
          Create a Loftie+ grant for this order
        </h5>
        <p className="text-muted mb-3">
          For an order that should carry Loftie+ but never got a contract: a
          2020 Indiegogo backer (order numbers like <code>igg-35</code>, tagged{" "}
          <code>indiegogo</code>, promised lifetime premium content), or a
          hardware order the webhook skipped. Paid subscriptions are never
          created here. The order number and the app email must both come from
          the customer's ticket.
        </p>

        {!createPreview && !createLoading && (
          <Button
            variant="outline-primary"
            style={{ borderColor: "#635956", color: "#635956" }}
            onClick={() => loadCreatePreview()}
          >
            Look up order {searchEmail.trim()} in Shopify
          </Button>
        )}

        {createLoading && (
          <div>
            <Spinner animation="border" size="sm" className="me-2" />
            Looking up the order…
          </div>
        )}

        {createError && (
          <Alert variant="danger" className="mt-3 mb-0">
            <strong>Not done.</strong> {createError}
          </Alert>
        )}

        {createPreview && (
          <>
            <Table
              size="sm"
              bordered
              className="mt-3 mb-3"
              style={{ backgroundColor: "white" }}
            >
              <tbody>
                <tr>
                  <th style={{ width: "28%" }}>Order</th>
                  <td>
                    {createPreview.order.name} · placed{" "}
                    {formatIsoDay(createPreview.order.processed_at)} ·{" "}
                    {createPreview.order.financial_status} /{" "}
                    {createPreview.order.fulfillment_status}
                  </td>
                </tr>
                <tr>
                  <th>Customer</th>
                  <td>
                    {createPreview.order.customer_name || "N/A"} ·{" "}
                    {createPreview.order.email || "no email on the order"}
                  </td>
                </tr>
                <tr>
                  <th>Items</th>
                  <td>
                    {createPreview.order.line_items.map((li, i) => (
                      <div key={i}>
                        {li.quantity} × {li.title}
                        {li.sku ? ` (${li.sku})` : ""}
                        {li.selling_plan
                          ? ` — selling plan: ${li.selling_plan}`
                          : ""}
                      </div>
                    ))}
                  </td>
                </tr>
                <tr>
                  <th>Tags</th>
                  <td>
                    {createPreview.order.tags.length
                      ? createPreview.order.tags.map((t) => (
                          <Badge
                            key={t}
                            bg={
                              t.toLowerCase() === "indiegogo"
                                ? "warning"
                                : "secondary"
                            }
                            text={
                              t.toLowerCase() === "indiegogo"
                                ? "dark"
                                : undefined
                            }
                            className="me-1"
                          >
                            {t}
                          </Badge>
                        ))
                      : "none"}
                  </td>
                </tr>
                <tr>
                  <th>App account for {previewEmail || "—"}</th>
                  <td>
                    {createPreview.app_account
                      ? `Yes (${createPreview.app_account.platform || "app"}; today ${
                          createPreview.app_account.membership_status ||
                          "no membership"
                        }${
                          createPreview.app_account.membership_inactive_date
                            ? `, ends ${formatIsoDay(
                                createPreview.app_account
                                  .membership_inactive_date
                              )}`
                            : ""
                        })`
                      : createPreview.app_account_count > 1
                      ? `${createPreview.app_account_count} accounts use this address`
                      : "None yet — they sign up with this exact address, then Link your purchase"}
                  </td>
                </tr>
              </tbody>
            </Table>

            {createPreview.blockers.length > 0 ? (
              <Alert variant="warning" className="mb-0">
                <strong>No grant can be created here.</strong>
                <ul className="mb-0 mt-1">
                  {createPreview.blockers.map((b, i) => (
                    <li key={i}>{b}</li>
                  ))}
                </ul>
              </Alert>
            ) : (
              <Form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleCreateGrant();
                }}
              >
                <Row className="g-2 align-items-end">
                  <Col md={2}>
                    <Form.Label className="mb-1">Term</Form.Label>
                    <Form.Select
                      value={createType}
                      onChange={(e) => setCreateType(e.target.value)}
                      required
                    >
                      <option value="">Choose…</option>
                      <option value="LIFETIME">Lifetime</option>
                      <option value="ANNUAL">Annual (one year)</option>
                    </Form.Select>
                  </Col>
                  <Col md={4}>
                    <Form.Label className="mb-1">App email</Form.Label>
                    <Form.Control
                      type="email"
                      required
                      value={createEmail}
                      onChange={(e) => setCreateEmail(e.target.value)}
                      placeholder="the address they log into the app with"
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Label className="mb-1">Name on contract</Form.Label>
                    <Form.Control
                      type="text"
                      value={createName}
                      onChange={(e) => setCreateName(e.target.value)}
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Label className="mb-1">Reason (recorded)</Form.Label>
                    <Form.Control
                      type="text"
                      required
                      value={createReason}
                      onChange={(e) => setCreateReason(e.target.value)}
                      placeholder="e.g. Indiegogo backer"
                    />
                  </Col>
                </Row>

                {typedEmail && typedEmail !== previewEmail && (
                  <Form.Text muted>
                    The account check above is for{" "}
                    {previewEmail || "the order email"}.{" "}
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 align-baseline"
                      disabled={createLoading}
                      onClick={() => loadCreatePreview(typedEmail)}
                    >
                      Re-check {typedEmail}
                    </Button>{" "}
                    before creating. The server re-checks either way.
                  </Form.Text>
                )}

                <div className="mt-3">
                  <Button
                    type="submit"
                    variant="info"
                    disabled={createBusy || !createType}
                    className="me-2"
                  >
                    {createBusy
                      ? "Working..."
                      : `Create ${termLabel} Loftie+ grant`}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    onClick={resetCreate}
                    disabled={createBusy}
                  >
                    Cancel
                  </Button>
                </div>
              </Form>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="SubscriptionContracts">
      <Container>
        <div style={{ height: "45px" }}></div>
        <h1 className="text-center mb-4" style={{ color: "#F0D6B5" }}>
          Subscription Contracts
        </h1>

        <Row className="justify-content-center">
          <Col md={10}>
            <Card>
              <Card.Body>
                <div className="text-center mb-4">
                  <FontAwesomeIcon
                    icon={faFileContract}
                    size="3x"
                    style={{ color: "#F0D6B5" }}
                  />
                </div>

                <Form.Group className="mb-3">
                  <Form.Label>Search by</Form.Label>
                  <Form.Select
                    value={searchBy}
                    onChange={(e) => {
                      setSearchBy(e.target.value);
                      setHasSearched(false);
                      setSearchResults([]);
                      setSearchEmail("");
                      resetCreate();
                    }}
                    className="mb-3"
                  >
                    <option value="email">Email address</option>
                    <option value="order">Order number</option>
                  </Form.Select>
                  <Form.Label>
                    {searchBy === "order" ? "Order Number" : "Email Address"}
                  </Form.Label>
                  <Form.Control
                    type={searchBy === "order" ? "text" : "email"}
                    value={searchEmail}
                    onChange={(e) => {
                      setSearchEmail(e.target.value);
                      // A new term is a new context; drop any half-finished grant preview.
                      if (createPreview || createError || createResult) resetCreate();
                    }}
                    onKeyPress={handleKeyPress}
                    placeholder={
                      searchBy === "order"
                        ? "e.g. #361649"
                        : "Enter email address"
                    }
                  />
                  {searchBy === "order" && (
                    <Form.Text muted>
                      Use the order number whenever the app email is not the
                      order email: a gift stranded on the ShopMy relay, or a
                      clock bought on one address and signed up on another. Then
                      “Apply to app account”. An order with no contract at all
                      (a 2020 Indiegogo backer nobody has granted yet; those
                      order numbers look like <code>igg-123</code>) gets a
                      “Create grant” option here.
                    </Form.Text>
                  )}
                </Form.Group>

                <div className="text-center">
                  <Button
                    variant="primary"
                    onClick={handleSearch}
                    disabled={loading}
                    style={{
                      backgroundColor: "#635956",
                      borderColor: "#635956",
                    }}
                  >
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Searching...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faSearch} className="me-2" />
                        Search
                      </>
                    )}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Error Alert */}
        {error && (
          <Row className="mt-3 justify-content-center">
            <Col md={10}>
              <Alert variant="danger">{error}</Alert>
            </Col>
          </Row>
        )}

        {/* Search Results */}
        {hasSearched && !loading && !error && (
          <Row className="mt-3 justify-content-center">
            <Col md={10}>
              <Card>
                <Card.Body>
                  <Card.Title className="text-center mb-4">
                    Search Results
                  </Card.Title>

                  {createResult && (
                    <Alert variant="success">
                      <strong>
                        {createResult.membership_type} Loftie+ is now on{" "}
                        {createResult.email}.
                      </strong>{" "}
                      Order {createResult.order}, contract{" "}
                      <code>{createResult.contract_id}</code>, created by{" "}
                      {createResult.granted_by}.
                      <div className="mt-1">
                        {createResult.has_app_account
                          ? "Their app account is switched on — have them force-quit and reopen the app, or sign out and back in."
                          : `They have no Loftie app account yet. They need to download the app, sign up with exactly that address, and use “Link your purchase” — ${createResult.membership_type} turns on from there.`}
                      </div>
                    </Alert>
                  )}

                  {searchResults.length === 0 ? (
                    <>
                      <Alert variant="info" className="text-center">
                        No contracts found for{" "}
                        {searchBy === "order" ? "order" : "email"}:{" "}
                        <strong>{searchEmail}</strong>
                      </Alert>
                      {searchBy === "order" && renderCreateGrant()}
                    </>
                  ) : (
                    <>
                      <Alert variant="success" className="text-center">
                        Found {searchResults.length} contract(s) for{" "}
                        {searchBy === "order" ? "order" : "email"}:{" "}
                        <strong>{searchEmail}</strong>
                      </Alert>

                      <div className="table-responsive">
                        <Table striped bordered hover>
                          <thead style={{ backgroundColor: "#F0D6B5" }}>
                            <tr>
                              <th>Email</th>
                              <th>Status</th>
                              <th>Plan Type</th>
                              <th>Amount</th>
                              <th>Created Date</th>
                              <th>Renews / Expires</th>
                              <th>Contract ID</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {searchResults.map((contract) => (
                              <React.Fragment key={contract.id}>
                              <tr>
                                <td>
                                  <strong style={{ color: "#635956" }}>
                                    {contract.user?.email || "N/A"}
                                  </strong>
                                  {isStranded(contract) && (
                                    <div>
                                      <Badge bg="warning" text="dark">
                                        stranded on ShopMy relay
                                      </Badge>
                                    </div>
                                  )}
                                </td>
                                <td>{formatStatus(contract.status)}</td>
                                <td>
                                  <span
                                    style={{
                                      fontWeight: "bold",
                                      color: "#635956",
                                    }}
                                  >
                                    {contract.type || "Not Listed"}
                                  </span>
                                </td>
                                <td>
                                  <strong style={{ color: "#635956" }}>
                                    {contract.amount
                                      ? `$${contract.amount}`
                                      : "N/A"}
                                  </strong>
                                </td>
                                <td>{formatDate(contract.createdAt)}</td>
                                <td>{formatDate(contract.nextBillingDate)}</td>
                                <td>
                                  <code style={{ fontSize: "0.8em" }}>
                                    {contract.id}
                                  </code>
                                </td>
                                <td>
                                  {canApply(contract) ? (
                                    <Button
                                      size="sm"
                                      variant="outline-info"
                                      disabled={reassignBusy}
                                      onClick={() =>
                                        openReassign(contract, "search")
                                      }
                                    >
                                      {isStranded(contract)
                                        ? "Reassign"
                                        : "Apply to app account"}
                                    </Button>
                                  ) : (
                                    <span
                                      className="text-muted"
                                      style={{ fontSize: "0.8em" }}
                                      title="A paid subscription, not a grant. It cannot be moved from here."
                                    >
                                      paid
                                    </span>
                                  )}
                                </td>
                              </tr>
                              {renderReassignPanel(contract, "search", 9)}
                              </React.Fragment>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Stranded ShopMy gifts */}
        <Row className="mt-3 justify-content-center">
          <Col md={10}>
            <Card>
              <Card.Body>
                <Card.Title>Stranded ShopMy Loftie+ gifts</Card.Title>
                <p className="text-muted mb-3">
                  Lifetime grants still sitting on a{" "}
                  <code>gifting+NNNNNN@shopmyshelf.us</code> relay address, so the
                  creator's own account never activates. Match a creator to a row
                  by order number from their ticket — never by name alone.
                </p>
                <Button
                  variant="outline-primary"
                  style={{ borderColor: "#635956", color: "#635956" }}
                  disabled={strandedLoading}
                  onClick={() => {
                    const next = !showStranded;
                    setShowStranded(next);
                    if (next && stranded.length === 0) loadStranded();
                  }}
                >
                  {strandedLoading
                    ? "Loading..."
                    : `${showStranded ? "Hide" : "Show"} stranded gifts`}
                </Button>

                {showStranded && !strandedLoading && (
                  <div className="table-responsive mt-3">
                    <Alert variant="secondary" className="text-center">
                      {stranded.length} contract(s) still on the ShopMy relay
                    </Alert>
                    <Table striped bordered hover>
                      <thead style={{ backgroundColor: "#F0D6B5" }}>
                        <tr>
                          <th>Relay Email</th>
                          <th>Status</th>
                          <th>Plan Type</th>
                          <th>Order</th>
                          <th>Renews / Expires</th>
                          <th>Contract ID</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stranded.map((contract) => (
                          <React.Fragment key={contract.id}>
                            <tr>
                              <td>
                                <code style={{ fontSize: "0.85em" }}>
                                  {contract.user?.email || "N/A"}
                                </code>
                              </td>
                              <td>{formatStatus(contract.status)}</td>
                              <td>
                                <span
                                  style={{
                                    fontWeight: "bold",
                                    color: "#635956",
                                  }}
                                >
                                  {contract.type || "Not listed"}
                                </span>
                              </td>
                              <td>{contract.order_id || "N/A"}</td>
                              <td>{formatDate(contract.nextBillingDate)}</td>
                              <td>
                                <code style={{ fontSize: "0.8em" }}>
                                  {contract.id}
                                </code>
                              </td>
                              <td>
                                {isStranded(contract) ? (
                                  <Button
                                    size="sm"
                                    variant="outline-info"
                                    disabled={reassignBusy}
                                    onClick={() =>
                                      openReassign(contract, "stranded")
                                    }
                                  >
                                    Reassign
                                  </Button>
                                ) : (
                                  <Badge bg="success">done</Badge>
                                )}
                              </td>
                            </tr>
                            {renderReassignPanel(contract, "stranded", 7)}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* All Contracts Toggle */}
        <Row className="mt-3 justify-content-center">
          <Col md={10}>
            <Card>
              <Card.Body>
                <div className="text-center mb-3">
                  <Button
                    variant="outline-primary"
                    onClick={() => setShowAllContracts(!showAllContracts)}
                    style={{
                      borderColor: "#635956",
                      color: "#635956",
                    }}
                  >
                    {showAllContracts ? "Hide" : "Show"} All Contracts
                  </Button>
                </div>

                {showAllContracts && (
                  <>
                    {contractsLoading ? (
                      <div className="text-center">
                        <Spinner animation="border" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </Spinner>
                        <p>Loading contracts...</p>
                      </div>
                    ) : contractsError ? (
                      <Alert variant="danger">
                        Error loading contracts: {contractsError.message}
                      </Alert>
                    ) : (
                      <>
                        <Alert variant="secondary" className="text-center">
                          Showing all {allContracts.length} contracts
                        </Alert>
                        <div className="table-responsive">
                          <Table striped bordered hover>
                            <thead style={{ backgroundColor: "#F0D6B5" }}>
                              <tr>
                                <th>Email</th>
                                <th>Status</th>
                                <th>Plan Type</th>
                                <th>Amount</th>
                                <th>Created Date</th>
                                <th>Renews / Expires</th>
                                <th>Contract ID</th>
                              </tr>
                            </thead>
                            <tbody>
                              {allContracts.map((contract) => (
                                <tr key={contract.id}>
                                  <td>
                                    <strong style={{ color: "#635956" }}>
                                      {contract.user?.email || "N/A"}
                                    </strong>
                                  </td>
                                  <td>{formatStatus(contract.status)}</td>
                                  <td>
                                    <span
                                      style={{
                                        fontWeight: "bold",
                                        color: "#635956",
                                      }}
                                    >
                                      {contract.type || "Not listed"}
                                    </span>
                                  </td>
                                  <td>
                                    <strong style={{ color: "#635956" }}>
                                      {contract.amount
                                        ? `$${contract.amount}`
                                        : "N/A"}
                                    </strong>
                                  </td>
                                  <td>{formatDate(contract.createdAt)}</td>
                                  <td>{formatDate(contract.nextBillingDate)}</td>
                                  <td>
                                    <code style={{ fontSize: "0.8em" }}>
                                      {contract.id}
                                    </code>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="mt-3 justify-content-center">
          <Col md={10}>
            <div className="text-center">
              <a href="/" style={{ color: "#635956", textDecoration: "none" }}>
                ← Back to Dashboard
              </a>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default SubscriptionContracts;
